import { useMemo, useState, useCallback } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { Button, Drawer, Form } from 'antd';
import SlideRulerModule from '@/components/slide-ruler';
import { DIMENSION_VALUE_CONFIG } from '@/pages/ai-consultation/components/body-dimension-selector';
import { storagePrefixFixedV2 } from '@/constant';
import cx from './index.module.scss';

const DIMENSION_IMG: Record<AIDiagnose.DimensionType, string> = {
  waistCircumference: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/waist.png',
  backThickness: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/back.png',
  leftArmCircumference: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/left-arm.png',
  rightArmCircumference: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/right-arm-new.png',
  leftCalfCircumference: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/left-calf.png',
  rightCalfCircumference: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/right-calf-new.png',
  leftThighCircumference: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/left-thigh.png',
  rightThighCircumference: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/right-thigh-new.png',
};

interface WeightLossItemProps {
  name: string;
  goalValue: number;
  value?: number;
  onChange?: (value: number) => void;
}

export const formatNumber = (value: number) => {
  return value % 10 === 0 ? Number(value / 10) : (value / 10).toFixed(1);
};

const WeightLossItem = (props: WeightLossItemProps) => {
  const { value, name, goalValue, onChange } = props;
  const [isDrawerShow, setIsDrawerShow] = useState<boolean>(false);
  const [form] = Form.useForm();

  const sliderVal = useMemo(() => {
    return formatNumber(value) || DIMENSION_VALUE_CONFIG[name]?.default;
  }, [name, value]);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerShow(false);
    onChange(Math.round(form?.getFieldValue(name) * 10));
  }, [form, name, onChange]);

  return (
    <div className={cx('item')}>
      <div className={cx('itm', 'goal')}>
        <div>{DIMENSION_VALUE_CONFIG[name]?.label + '目标'}</div>
        <div className={cx('value')}>{goalValue ? formatNumber(goalValue) + '厘米' : '暂无'}</div>
      </div>
      <div className={cx('itm', 'current')} onClick={() => setIsDrawerShow(true)}>
        <div className={cx('current-value')}>
          <div>当前</div>
          <div className={cx('value')}>{value ? formatNumber(value) + '厘米' : '暂无'}</div>
        </div>

        <Icon name="arrow-right" className={cx('icon')} />
      </div>
      <Drawer
        className={cx('slide-ruler-drawer')}
        onClose={() => {
          setIsDrawerShow(false);
          form.resetFields();
        }}
        maskClosable={false}
        width="520px"
        destroyOnClose
        title={<span className={cx('drawer-title')}>{'输入当前' + DIMENSION_VALUE_CONFIG[name]?.label}</span>}
        styles={{
          header: { background: '#E6E8EB' },
          body: { background: '#E6E8EB' },
        }}
        closeIcon={<Icon name="close-circle" className={cx('close-drawer')} color="#b3b4b6" />}
        open={isDrawerShow}
      >
        <div className={cx('content')}>
          <div className={cx('form')}>
            <Form form={form} clearOnDestroy>
              <Form.Item shouldUpdate noStyle>
                {() => (
                  <div className={cx('img-wrapper')}>
                    <img src={DIMENSION_IMG[name]} />
                    <div className={cx('value-desc', name)}>
                      <div>{DIMENSION_VALUE_CONFIG[name]?.label}</div>
                      <div className={cx('current-value')}>{form.getFieldsValue()?.[name]}</div>
                      <div>厘米</div>
                    </div>
                  </div>
                )}
              </Form.Item>

              <Form.Item name={name} noStyle initialValue={sliderVal}>
                <SlideRulerModule
                  label={DIMENSION_VALUE_CONFIG[name]?.label}
                  defaultVal={DIMENSION_VALUE_CONFIG[name]?.default}
                  unit="厘米"
                  maxValue={DIMENSION_VALUE_CONFIG[name]?.max}
                  minValue={DIMENSION_VALUE_CONFIG[name]?.min}
                />
              </Form.Item>
            </Form>
          </div>

          <div className={cx('note-text')}>为提升人体识别确切性，优化顾客体感，请准确选择上述身体信息。</div>

          <div className={cx('slide-ruler-footer')}>
            <Button color="primary" className={cx('confirm-btn')} onClick={handleCloseDrawer}>
              确定
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default WeightLossItem;
